import { uuid } from "geotab-uuid";
import { IApiError } from "./api.interfaces";
import { dispatcher, DispatcherEvents } from "./dispatcher";
import { IAdditionalProperties, IBrowserInfo, IVersionInfo } from "./error.impl";

export enum LogType {
    LOG = "LOG",
    ERROR = "ERROR",
    TIMER = "TIMER",
    EVENT = "EVENT",
    FEEDBACK = "FEEDBACK",
    SURVEY = "SURVEY"
}

export enum Source {
    ROUTER = "ROUTER",
    ERROR = "ERROR",
    UI = "UI",
    IOX = "IOX",
    SYNC = "SYNC",
    SYNCERROR = "SYNCERROR",
    SYNCDIGEST = "SYNCDIGEST",
    SERVICES = "SERVICES",
    USER = "USER",
    ENTITY = "ENTITY",
    DEBUG = "DEBUG",
    API = "API",
    ADDIN = "ADDIN",
    NATIVE = "NATIVE",
    APPCACHE = "APPCACHE",
    GEO = "GEO",
    CHANNEL = "CHANNEL",
    USER_EVENT = "USER_EVENT",
    SERVWORKER = "SERVWORKER",
    NATIVE_SDK_ERROR = "NATIVE_SDK_ERROR",
    NATIVE_SDK_WARN = "NATIVE_SDK_WARN",
    NATIVE_SDK_INFO = "NATIVE_SDK_INFO",
    OPENCAB = "OPENCAB",
    UNTRANSLATED_STRING = "UNTRANSLATED_STRING",
    ADDIN_STARTUP = "ADDIN_STARTUP",
    STORAGE = "STORAGE",
    LAUNCHER = "LAUNCHER",
    ADDIN_STORAGE_AUDIT = "ADDIN_STORAGE_AUDIT",
    IAM = "IAM",
    PERFORMANCE = "PERFORMANCE",
    ERROR_TRACKER = "ERROR_TRACKER",
    WEBSOCKET_ACK = "WEBSOCKET_ACK",
    INDEXED_DB = "INDEXED_DB"
}

export enum Event {
    FOREGROUND = "FOREGROUND",
    BACKGROUND = "BACKGROUND",
    BACKGROUNDFAILURE = "BACKGROUNDFAILURE",
    NETWORKOFFLINE = "NETWORKOFFLINE",
    NETWORKONLINE = "NETWORKONLINE",
    GPSCONNECT = "GPSCONNECT",
    GPSFAILURE = "GPSFAILURE",
    MOTIONCHANGE = "MOTIONCHANGE",
    RESTARTTRACKING = "RESTARTTRACKING",
    CHARGING = "CHARGING",
    BATTERY = "BATTERY",
    LOGINTIME = "LOGINTIME",
    APPCACHEDOWNLOADTIME = "APPCACHEDOWNLOADTIME",
    SYNCHOSCONFLICT = "SYNCHOSCONFLICT",
    // HOS sync tracing -- emitted when a pending record is retried, not on first push.
    SYNCHOSPUSHRETRY = "SYNCHOSPUSHRETRY",
    COMPLIANCEDISPLAY = "COMPLIANCEDISPLAY",
    COMPLIANCETRANSFER = "COMPLIANCETRANSFER",
    ADDDRIVERCHANGE = "ADDDRIVERCHANGE",
    SUSPICIOUSLOGDISTANCE = "SUSPICIOUSLOGDISTANCE",
    EXCESSIVEDUTYSTATUSLOGS = "EXCESSIVEDUTYSTATUSLOGS",
    IOXATTACHED = "IOXATTACHED",
    IOXDETACHED = "IOXDETACHED",
    IOXRAWDATA = "IOXRAWDATA",
    IOXGENERATEDDSL = "IOXGENERATEDDSL",
    CLOCKDRIFT = "CLOCKDRIFT",
    IMAGEACTION = "IMAGEACTION",
    SYNCMESSAGESCONFLICT = "SYNCMESSAGESCONFLICT",
    PERFORMANCE = "PERFORMANCE",
    SYNCTRIPCONFLICT = "SYNCTRIPCONFLICT",
    SYNCTRIPANNOTATIONCONFLICT = "SYNCTRIPANNOTATIONCONFLICT",
    SYNCGROUPDEVICECONFLICT = "SYNCGROUPDEVICECONFLICT",
    FILEACTION = "FILEACTION",
    RATELIMITING = "RATELIMITING",
    SSOPAGELAUNCH = "SSOPAGELAUNCH",
    PCEXCEEDCONDITION = "PCEXCEEDCONDITION",
    TEMPDEBUG = "TEMPDEBUG",
    SYNCCOMPLETE = "SYNCCOMPLETE",
    OPENCABLOGIN = "OPENCABLOGIN",
    ADDINCALL = "ADDINCALL",
    AUTHENTICATION = "AUTHENTICATION",
    SOURCECODEVERSION = "SOURCECODEVERSION",
    DEBUG_LOGINVIEW_NAVIGATE = "DEBUG_LOGINVIEW_NAVIGATE",
    ADDIN_LIFECYCLE_COUNTS = "ADDIN_LIFECYCLE_COUNTS",
    HOSLOGRETENTIONPRUNED = "HOSLOGRETENTIONPRUNED",
    DRIVERLOGPROMPT = "DRIVERLOGPROMPT",
    HISTORICALDAYADOPTION = "HISTORICALDAYADOPTION",
    HISTORICALDAYCACHE = "HISTORICALDAYCACHE",
    HISTORICALDAYAPI = "HISTORICALDAYAPI",
    // MYG-147181 / MYG-147182: logging-only diagnostics for logout DriverChange/VehicleRemoved races (see logoutRaceDiagnostics.ts)
    LOGOUTRACE = "LOGOUTRACE"
}

export interface ILog {
    type: string;
    date: string;
    source: string;
    message: string;
    user: string;
    instanceId: string;
    deviceUuid?: string;
    dateEnd?: string;
    occurs?: number;
    duration?: number;
    connectionType?: string;
    additionalDetails?: AdditionalDetails;
    lastLogs?: ILog[];
    sequence?: number;
}

export class AdditionalDetails {
    userName?: string;
    database?: string;
    browserInfo?: IBrowserInfo;
    versionInfo?: IVersionInfo;
    additionalProps: IAdditionalProperties & { appCacheStatus: string };
}

export function log(from: Source, message: string | string[], user?: string | any): void {
    dispatcher.trigger(DispatcherEvents.LoggerLog, LogType.LOG, from, message, user);
}

export function error(from: Source, message: string | string[], user?: string | any, errorObject?: IApiError): void {
    dispatcher.trigger(DispatcherEvents.LoggerLog, LogType.ERROR, from, message, user, undefined, undefined, undefined, errorObject);
}

export function event(from: Event, message: string | string[], user?: string | any, duration: number | any = null, connectionType: string | any = null): void {
    dispatcher.trigger(DispatcherEvents.LoggerLog, LogType.EVENT, from, message, user, duration, connectionType);
}

export function time(from: Source, id: string) {
    dispatcher.trigger(DispatcherEvents.LoggerTime, from, id);
}

export function timeEnd(from: Source, id: string, message: string, user?: string | any): void {
    dispatcher.trigger(DispatcherEvents.LoggerTimeEnd, from, id, message, user);
}

export function timer(from: Source, message: string, user?: string | any) {
    const taskId = uuid();
    dispatcher.trigger(DispatcherEvents.LoggerTime, from, taskId);
    return (isSuccessful?: boolean) => {
        dispatcher.trigger(DispatcherEvents.LoggerTimeEnd, from, taskId, message, user, typeof isSuccessful === "undefined" || isSuccessful);
    };
}

export function feedback(description: string, additionalDetails: AdditionalDetails) {
    dispatcher.trigger(DispatcherEvents.LoggerFeedback, description, additionalDetails);
}

export function survey(from: Source, message: string | string[], user?: string | any): void {
    dispatcher.trigger(DispatcherEvents.LoggerLog, LogType.SURVEY, from, message, user);
}
